/**
 * 프로필 관련 유틸리티 및 상수
 *
 * 여러 컴포넌트에서 중복 정의되던 기본 프로필 이미지 URL,
 * 이니셜 추출, 프로필 배경색 생성 등을 통합.
 */
import apiConfig from '@/api/config';

const STATIC_ASSET_BASE_URL = 'https://static.wehago.com';

/** 기본 프로필 이미지 URL */
export const DEFAULT_PROFILE = `${STATIC_ASSET_BASE_URL}/imgs/common/profile_icon_user.png`;

/** WEHAGO 빈 프로필 이미지 URL */
export const EMPTY_PROFILE = `${STATIC_ASSET_BASE_URL}/imgs/rtc/empty_profile.png`;

/** 이름에서 이니셜 추출 */
export const getInitial = (name: string): string => {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
};

/** 이름 기반 프로필 배경색 생성 */
export const getProfileColor = (name: string): string => {
  const colors = [
    '#d37ebb', '#7ebed3', '#d3a87e', '#7ed3a8',
    '#a87ed3', '#d37e7e', '#7e97d3', '#b3d37e',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const HAS_SCHEME = /^[a-z][a-z\d+\-.]*:/i;

const isEmptyProfileUrl = (url: string) => {
  const normalized = url.trim().toLowerCase();
  return !normalized || normalized === 'null' || normalized === 'undefined' || normalized === 'none';
};

const normalizePath = (path: string) => `/${path.replace(/^\/+/, '')}`;

const toPortalUrl = (path: string) => `${apiConfig.portalBaseUrl}${normalizePath(path)}`;

/** 프로필 URL을 포털 도메인의 절대경로로 변환 */
export const toAbsoluteProfileUrl = (url?: string | null): string | undefined => {
  const normalized = String(url ?? '').trim();
  if (isEmptyProfileUrl(normalized)) return undefined;

  if (normalized.startsWith('//')) {
    return `https:${normalized}`;
  }

  if (HAS_SCHEME.test(normalized)) {
    return normalized;
  }

  return toPortalUrl(normalized);
};

/** 프로필 사진 표시용 URL. profileUrl만 도메인을 보정하고 avatarUrl은 원문 fallback으로 유지한다. */
export const toDisplayProfileUrl = (
  profileUrl?: string | null,
  avatarUrl?: string | null,
): string | undefined => {
  const absoluteProfileUrl = toAbsoluteProfileUrl(profileUrl);
  if (absoluteProfileUrl) return absoluteProfileUrl;

  const rawAvatarUrl = String(avatarUrl ?? '').trim();
  return isEmptyProfileUrl(rawAvatarUrl) ? undefined : rawAvatarUrl;
};

/** 프로필 URL을 포털 도메인의 썸네일 절대경로로 변환 */
export const toProfileThumbnailUrl = (url?: string | null): string | undefined => {
  const absoluteUrl = toAbsoluteProfileUrl(url);
  if (!absoluteUrl) return undefined;

  return absoluteUrl.replace(/([^/?#]+?)(\.[^./?#]+)([?#].*)?$/, (match, name, extension, suffix = '') => {
    if (name.endsWith('_ss')) return match;
    return `${name}_ss${extension}${suffix}`;
  });
};
